import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  SafeAreaView,
  Platform,
} from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useTheme } from '../screens/ThemeContext';

const PolicySection = ({ title, children, theme }) => (
  <View style={styles.section}>
    <Text style={[styles.sectionTitle, { color: theme.text }]}>{title}</Text>
    <View style={[styles.sectionContent, { backgroundColor: theme.surface }]}>
      {children}
    </View>
  </View>
);

const PolicyItem = ({ content, theme }) => (
  <Text style={[styles.policyText, { color: theme.textSecondary }]}>{content}</Text>
);

const PrivacyPolicyScreen = ({ navigation }) => {
  const { theme } = useTheme();

  return (
    <SafeAreaView style={[styles.safeArea, { backgroundColor: theme.background }]}>
      <View style={[styles.header, { backgroundColor: theme.background }]}>
        <TouchableOpacity 
            onPress={() => navigation.goBack()}
            style={[styles.backButton, { backgroundColor: theme.surface, borderColor: theme.border }]}
          >
            <MaterialCommunityIcons 
              name="arrow-left" 
              size={24} 
              color={theme.primary} 
            />
        </TouchableOpacity>
        <Text style={[styles.headerTitle, { color: theme.text }]}>
        Politique de Confidentialité
        </Text>
      </View>

      <ScrollView style={styles.container} showsVerticalScrollIndicator={false}>
        <View style={styles.content}>
          {/* Introduction */}
          <PolicySection title="Introduction" theme={theme}>
            <PolicyItem
              theme={theme}
              content="Cette politique de confidentialité décrit la manière dont MyConcours collecte, utilise et protège vos informations personnelles. Nous accordons une grande importance à la protection de votre vie privée."
            />
          </PolicySection>

          {/* Collecte des Informations */}
          <PolicySection title="Collecte des Informations" theme={theme}>
            <PolicyItem
              theme={theme}
              content="Nous collectons les informations suivantes :"
            />
            <PolicyItem
              theme={theme}
              content="• Informations de compte (nom, email, numéro de téléphone)"
            />
            <PolicyItem
              theme={theme}
              content="• Historique des achats et téléchargements"
            />
            <PolicyItem
              theme={theme}
              content="• Préférences de notification"
            />
            <PolicyItem
              theme={theme}
              content="• Données d'utilisation de l'application"
            />
          </PolicySection>

          {/* Utilisation des Informations */}
          <PolicySection title="Utilisation des Informations" theme={theme}>
            <PolicyItem
              theme={theme}
              content="Nous utilisons vos informations pour :"
            />
            <PolicyItem
              theme={theme}
              content="• Gérer votre compte et fournir nos services"
            />
            <PolicyItem
              theme={theme}
              content="• Personnaliser votre expérience"
            />
            <PolicyItem
              theme={theme}
              content="• Traiter vos paiements"
            />
            <PolicyItem
              theme={theme}
              content="• Communiquer des mises à jour importantes"
            />
          </PolicySection>

          {/* Protection des Données */}
          <PolicySection title="Protection des Données" theme={theme}>
            <PolicyItem
              theme={theme}
              content="Nous protégeons vos données par :"
            />
            <PolicyItem
              theme={theme}
              content="• Le cryptage des données sensibles"
            />
            <PolicyItem
              theme={theme}
              content="• Des mesures de sécurité techniques avancées"
            />
            <PolicyItem
              theme={theme}
              content="• Des accès restreints aux informations personnelles"
            />
          </PolicySection>

          {/* Partage des Informations */}
          <PolicySection title="Partage des Informations" theme={theme}>
            <PolicyItem
              theme={theme}
              content="Nous ne partageons vos informations qu'avec :"
            />
            <PolicyItem
              theme={theme}
              content="• Les centres de préparation (uniquement avec votre accord)"
            />
            <PolicyItem
              theme={theme}
              content="• Nos prestataires de services (paiement, hébergement)"
            />
            <PolicyItem
              theme={theme}
              content="• Les autorités légales si requis par la loi"
            />
          </PolicySection>

          {/* Vos Droits */}
          <PolicySection title="Vos Droits" theme={theme}>
            <PolicyItem
              theme={theme}
              content="Vous avez le droit de :"
            />
            <PolicyItem
              theme={theme}
              content="• Accéder à vos données personnelles"
            />
            <PolicyItem
              theme={theme}
              content="• Rectifier vos informations"
            />
            <PolicyItem
              theme={theme}
              content="• Supprimer votre compte"
            />
            <PolicyItem
              theme={theme}
              content="• Limiter l'utilisation de vos données"
            />
          </PolicySection>

          {/* Cookies et Traceurs */}
          <PolicySection title="Cookies et Traceurs" theme={theme}>
            <PolicyItem
              theme={theme}
              content="Nous utilisons des cookies pour :"
            />
            <PolicyItem
              theme={theme}
              content="• Améliorer la performance de l'application"
            />
            <PolicyItem
              theme={theme}
              content="• Analyser l'utilisation du service"
            />
            <PolicyItem
              theme={theme}
              content="• Personnaliser votre expérience"
            />
          </PolicySection>

          {/* Modifications de la Politique */}
          <PolicySection title="Modifications de la Politique" theme={theme}>
            <PolicyItem
              theme={theme}
              content="Nous nous réservons le droit de modifier cette politique à tout moment. Les changements importants seront notifiés aux utilisateurs."
            />
          </PolicySection>

          {/* Contact */}
          <PolicySection title="Contact" theme={theme}>
            <PolicyItem
              theme={theme}
              content="Pour toute question concernant cette politique de confidentialité, contactez notre responsable de la protection des données à privacy@myconcours.com"
            />
          </PolicySection>

          {/* Date de mise à jour */}
          <Text style={[styles.updateDate, { color: theme.textSecondary }]}>
            Dernière mise à jour : Février 2024
          </Text>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0, 0, 0, 0.1)',
  },
  backButton: {
    padding: 8,
    marginRight: 16,
    borderRadius: 12,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
  },
  container: {
    flex: 1,
  },
  content: {
    padding: 20,
  },
  section: {
    marginBottom: 20,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 10,
  },
  sectionContent: {
    padding: 15,
    borderRadius: 12,
  },
  policyText: {
    fontSize: 14,
    lineHeight: 22,
    marginBottom: 10,
  },
  updateDate: {
    textAlign: 'center',
    fontSize: 14,
    marginTop: 20,
    marginBottom: 40,
  },
});

export default PrivacyPolicyScreen;